import React, { useState, useRef, useEffect, useCallback } from 'react';
import useToast from '../../hooks/useToast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import sessionService from '../../utils/sessionService';
import * as notificationService from '../../utils/notificationService';
import CreateProjectModal from '../modals/CreateProjectModal';
import CreateOrganizationModal from '../modals/CreateOrganizationModal';
import { useUserProfile } from '../../hooks/useUserProfile';
import {
  dispatchProjectCreated,
} from '../../utils/projectEventService';
import apiService from '../../utils/apiService';
import logoutService from '../../utils/logoutService.js';
import dataLoadingService from '../../utils/dataLoadingService.js';
import NotificationBell from './NotificationBell.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import InitialsBadge from './InitialsBadge';
import Image from '../AppImage';


const RoleBasedHeader = ({
  userRole = 'member',
  currentUser: propCurrentUser,
  currentOrganization: propCurrentOrganization,
  disableAsync = false,
}) => {
  const { showToast } = useToast();
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] =
    useState(false);
  const [isCreateOrganizationModalOpen, setIsCreateOrganizationModalOpen] =
    useState(false);
  const [canCreateProjects, setCanCreateProjects] = useState(true);
  const [isSwitchingProject, setIsSwitchingProject] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const orgDropdownRef = useRef(null);
  const projectDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);


  // Use centralized user profile hook for consistent data
  const { userProfile, currentOrganization, availableOrganizations, userRole: hookUserRole } =
    useUserProfile();

  // Use prop data if provided, otherwise use hook data
  const currentUser = propCurrentUser || userProfile;

  // Debug logging for user profile
  useEffect(() => {
    console.log('🔍 HEADER DEBUG: User profile data:', {
      propCurrentUser,
      userProfile,
      currentUser,
      displayName: currentUser?.displayName,
      email: currentUser?.email,
      firstName: currentUser?.firstName,
      lastName: currentUser?.lastName
    });
  }, [propCurrentUser, userProfile, currentUser]);

  // Always prefer hook data over prop data for organization
  const organization = currentOrganization || propCurrentOrganization;

  // Use the current user data
  const user = currentUser;

  // Get the actual user role from multiple sources
  const getActualUserRole = () => {
    let detectedRole = null; // No default fallback

    // Check userProfile.role first (most reliable)
    if (userProfile?.role) {
      detectedRole = userProfile.role.toLowerCase();
    }
    // Check currentUser.role as fallback
    else if (currentUser?.role) {
      detectedRole = currentUser.role.toLowerCase();
    }
    // Check localStorage for stored user role as last resort
    else {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser.role) {
          detectedRole = storedUser.role.toLowerCase();
        }
      } catch (e) {
        console.warn('Failed to parse stored user data:', e);
      }
    }

    console.log('🔍 Header - detected role:', detectedRole);
    console.log('🔍 Header - currentUser:', currentUser);
    console.log('🔍 Header - userProfile:', userProfile);

    return detectedRole;
  };

  const actualUserRole = hookUserRole?.toLowerCase() || userRole?.toLowerCase() || getActualUserRole();

  // Project state and data
  const [currentProject, setCurrentProject] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);

  // Function to load projects (extracted for reuse)
  const loadProjects = useCallback(async () => {
    if (!organization?.id) {
      return;
    }

    try {
      // Import apiService dynamically to avoid circular imports
      const { default: apiService } = await import('../../utils/apiService');
      const projects = await apiService.projects.getAll(organization.id);

      const transformedProjects = (projects || []).map((project) => ({
        id: project.id,
        name: project.name || 'Untitled Project',
        description: project.description || '',
        status: project.status || 'active',
      }));

      setAvailableProjects(transformedProjects);

      // Set current project from localStorage or first project
      const savedProjectId = localStorage.getItem('currentProjectId');
      if (savedProjectId) {
        const savedProject = transformedProjects.find(
          (p) => p.id === savedProjectId
        );
        if (savedProject) {
          setCurrentProject(savedProject);
        }
      } else if (transformedProjects.length > 0) {
        setCurrentProject(transformedProjects[0]);
        localStorage.setItem('currentProjectId', transformedProjects[0].id);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setAvailableProjects([]);
    }
  }, [organization?.id]);

  // Load projects when organization changes
  useEffect(() => {
    if (disableAsync || !organization?.id) return;
    loadProjects();
  }, [organization?.id, disableAsync, loadProjects]);

  // Auto-refresh projects list every 5 minutes
  useEffect(() => {
    if (disableAsync || !organization?.id) return;

    const PROJECTS_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing projects list in header...');
      loadProjects();
    }, PROJECTS_REFRESH_INTERVAL);

    return () => clearInterval(refreshInterval);
  }, [organization?.id, disableAsync, loadProjects]);

  // Listen for project updates from other components
  useEffect(() => {
    if (disableAsync) return;
    const handleProjectsUpdated = (event) => {
      const { organizationId, projects } = event.detail;
      if (organizationId === organization?.id) {
        console.log('Received projects update event:', projects);
        loadProjects(); // Refresh projects list
      }
    };

    window.addEventListener('projectsUpdated', handleProjectsUpdated);
    return () =>
      window.removeEventListener('projectsUpdated', handleProjectsUpdated);
  }, [organization?.id, disableAsync, loadProjects]);

  // Check organization settings for admin project creation permissions
  useEffect(() => {
    const checkAdminPermissions = async () => {
      if (disableAsync || !actualUserRole || !organization?.id) return;

      if (actualUserRole === 'admin') {
        try {
          const orgSettings = await apiService.organizations.getSettings(organization.id);
          const adminCanCreate = orgSettings?.allow_admin_create_projects === true;
          setCanCreateProjects(adminCanCreate);
        } catch (error) {
          console.error('Error loading organization settings:', error);
          // Default to false for admins if settings can't be loaded
          setCanCreateProjects(false);
        }
      } else {
        // Owners and other roles can create projects by default
        setCanCreateProjects(actualUserRole === 'owner');
      }
    };

    checkAdminPermissions();
  }, [actualUserRole, organization?.id, disableAsync]);

  // Role-based navigation configuration
  const getNavigationItems = (role) => {
    // Handle null or undefined role
    if (!role) {
      return [];
    }
    const baseItems = [
      {
        label: 'Dashboard',
        path: '/role-based-dashboard',
        icon: 'Home',
        roles: ['member', 'admin', 'owner'],
      },
      {
        label: 'Projects',
        path: '/kanban-board',
        icon: 'Kanban',
        roles: ['member', 'admin', 'owner'],
      },
      {
        label: 'Team Members',
        path: '/team-members',
        icon: 'Users',
        roles: ['member', 'admin', 'owner'],
      },
    ];

    const adminItems = [
      // Organization settings moved to owner-only items
    ];

    const ownerItems = [
      {
        label: 'Organization',
        path: '/organization-settings',
        icon: 'Settings',
        roles: ['owner'],
      },
    ];

    // Filter items based on user role
    const allItems = [...baseItems, ...adminItems, ...ownerItems];
    return allItems.filter((item) => item.roles.includes(role.toLowerCase()));
  };

  // Get role-specific features
  const getRoleFeatures = (role) => {
    // Handle null or undefined role
    if (!role) {
      role = 'member';
    }
    const features = {
      member: {
        canCreateProjects: true,
        canInviteMembers: false,
        canManageSettings: false,
        canViewAnalytics: false,
        canManageBilling: false,
        canSwitchOrganizations: true,
      },
      admin: {
        canCreateProjects: canCreateProjects,
        canInviteMembers: true,
        canManageSettings: true,
        canViewAnalytics: false,
        canManageBilling: false,
        canSwitchOrganizations: true,
      },
      owner: {
        canCreateProjects: true,
        canInviteMembers: true,
        canManageSettings: true,
        canViewAnalytics: true,
        canManageBilling: true,
        canSwitchOrganizations: true,
      },
    };
    return features[role.toLowerCase()] || features.member;
  };

  const navigationItems = getNavigationItems(actualUserRole);
  const roleFeatures = getRoleFeatures(actualUserRole);









  const isActivePath = (path) => location.pathname === path;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target)
      ) {
        setIsOrgDropdownOpen(false);
      }
      if (
        projectDropdownRef.current &&
        !projectDropdownRef.current.contains(event.target)
      ) {
        setIsProjectDropdownOpen(false);
      }
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setIsUserDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }

    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOrganizationSwitch = async (orgId) => {
    try {
      console.log('🚀 HEADER: Switching to organization:', orgId);

      // Find the organization in available organizations
      const selectedOrg = availableOrganizations.find(
        (org) => org.id === orgId
      );

      if (selectedOrg) {
        // Use centralized session service for consistent state
        sessionService.setOrganization(selectedOrg);

        // Redirect to dashboard for the new organization
        console.log('🔄 HEADER: Redirecting to dashboard...');
        window.location.href = getRoleDashboardPath();
      }

      setIsOrgDropdownOpen(false);
    } catch (error) {
      console.error('Failed to switch organization:', error);
      setIsOrgDropdownOpen(false);
    }
  };

  const handleProjectSwitch = (project) => {
    console.log('🔄 Header: Switching to project:', project);
    setIsSwitchingProject(true);

    // Save selected project to localStorage for persistence
    localStorage.setItem('currentProjectId', project.id);
    localStorage.setItem('currentProject', JSON.stringify(project));

    // Force a full page navigation to ensure data loads correctly
    window.location.href = `/project-management?id=${project.id}`;
  };



  const handleCreateProjectSubmit = async (projectData) => {
    try {
      console.log('Creating project:', projectData);
      console.log('Current organization:', organization);

      // Get organization ID - try multiple sources
      const orgId =
        organization?.id ||
        sessionService.getOrganizationId() ||
        sessionStorage.getItem('organizationId');
      console.log('Using organization ID:', orgId);

      if (!orgId) {
        throw new Error(
          'No organization ID available. Please refresh the page and try again.'
        );
      }

      // Create project via API
      const apiService = (await import('../../utils/realApiService')).default;
      const result = await apiService.projects.create(orgId, projectData);
      console.log('Project created via API:', result);

      const projectResult = result.data || result;
      const newProject = {
        id: projectResult.id,
        name: projectResult.name || projectData.name,
        description: projectResult.description || projectData.description,
        status: projectResult.status || 'active',
        memberRole: 'assigned',
      };

      // Refresh the projects list to include the new project
      await loadProjects();

      // Dispatch global project creation event
      console.log('🔔 Dispatching project created event:', {
        orgId,
        newProject,
      });
      if (orgId && newProject) {
        dispatchProjectCreated(orgId, newProject);
      } else {
        console.warn(
          '⚠️ Cannot dispatch project created event - missing orgId or newProject'
        );
      }

      // Update current project to the newly created one
      setCurrentProject(newProject);
      localStorage.setItem('currentProjectId', newProject.id);
      localStorage.setItem('currentProject', JSON.stringify(newProject));

      // Post-create: create in-app notification for the owner/admins
      try {
        const notifPayload = {
          user_id: user?.id || (userProfile && userProfile.id),
          organization_id:
            organization?.id || sessionService.getOrganizationId() || undefined,
          title: 'Project Created',
          message: `Project "${newProject.name}" has been created successfully`,
          type: 'project',
          priority: 'normal',
          action_url: `/project-management?id=${newProject.id}`,
          notification_metadata: { project_id: newProject.id },
        };
        const notifApi = (await import('../../utils/realApiService')).default;
        await notifApi.notifications.create(notifPayload);
      } catch (e) {
        console.warn('Post-create notification failed (non-blocking):', e);
      }

      setIsCreateProjectModalOpen(false);

      // Navigate to the new project's management page
      navigate(`/project-management?id=${newProject.id}`, {
        state: {
          projectId: newProject.id,
          project: newProject,
        },
      });
    } catch (error) {
      console.error('Failed to create project:', error);

      // Provide user-friendly error message
      let errorMessage = 'Failed to create project. ';

      if (error.status === 403 || error.message.includes('Permission denied') || error.message.includes('create_project')) {
        errorMessage = 'Project creation is restricted. Contact your organization owner to enable admin project creation.';
      } else if (error.message.includes('organization')) {
        errorMessage += 'Please refresh the page and try again.';
      } else if (
        error.message.includes('401') ||
        error.message.includes('unauthorized')
      ) {
        errorMessage += 'Please log in again.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }

      // You could show this error in a toast notification
      showToast(errorMessage, 'error');
      throw error;
    }
  };

  const handleCreateOrganization = () => {
    console.log('Creating new organization...');
    setIsOrgDropdownOpen(false);
    setIsCreateOrganizationModalOpen(true);
  };

  const handleCreateOrganizationSubmit = async (organizationData, logoFile) => {
    try {
      console.log('🚀 HEADER: Creating organization:', organizationData);
      
      // Import apiService dynamically to avoid potential circular dependencies
      const { default: apiService } = await import('../../utils/apiService');
      
      const result = await apiService.organizations.create(organizationData, logoFile);
      
      if (result.error) {
        throw new Error(result.error);
      }

      console.log('✅ HEADER: Organization created successfully:', result.data);

      // Update sessionService with new organization data if possible
      try {
        if (result.data) {
          const currentSession = sessionService.currentSession || {};
          const organizations = currentSession.organizations || [];
          
          // Add newly created organization to the list
          sessionService.currentSession = {
            ...currentSession,
            organizations: [...organizations, result.data]
          };
          
          // Persist changes
          sessionService.storeSession();
          console.log('✅ HEADER: Session updated with new organization');
        }
      } catch (sessionError) {
        console.warn('⚠️ HEADER: Failed to update session data (non-blocking):', sessionError);
      }

      setIsCreateOrganizationModalOpen(false);

      // Navigate or reload
      // A reload is safest to ensure all context-aware components (like useUserProfile) refresh
      console.log('🔄 HEADER: Reloading page to reflect new organization...');
      window.location.reload();

    } catch (error) {
      console.error('❌ HEADER: Failed to create organization:', error);
      showToast(error.message || 'Failed to create organization. Please try again.', 'error');
      throw error;
    }
  };

  const getRoleDashboardPath = () => {
    // Always redirect to role-based-dashboard regardless of role
    return '/role-based-dashboard';
  };

  const handleLogout = async () => {
    try {
      console.log('🚪 HEADER: Starting comprehensive logout process...');

      // Close any open dropdowns first
      setIsUserDropdownOpen(false);
      setIsOrgDropdownOpen(false);
      setIsProjectDropdownOpen(false);
      setIsMobileMenuOpen(false);

      // Use the comprehensive logout service
      await logoutService.logout({
        reason: 'user_initiated_header',
        showLoading: true,
        redirectUrl: '/login'
      });

      console.log('✅ HEADER: Comprehensive logout completed successfully');
    } catch (error) {
      console.error('❌ HEADER: Error during logout:', error);

      // Fallback to emergency logout if comprehensive logout fails
      console.log('🚨 HEADER: Attempting emergency logout...');
      logoutService.emergencyLogout();
    }
  };

  // Don't render header on auth pages
  if (location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    if (!role) {
      return 'bg-gray-100 text-gray-600'; // Style for no role
    }
    const colors = {
      member: 'bg-blue-100 text-blue-800',
      admin: 'bg-purple-100 text-purple-800',
      owner: 'bg-green-100 text-green-800',
    };
    return colors[role.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-white/50 dark:border-slate-800/50 shadow-sm">
      <div className='flex items-center justify-between h-20 px-4 lg:px-8'>
        {/* Logo */}
        <div className='flex items-center'>
          <Link
            to={getRoleDashboardPath()}
            className='flex items-center space-x-3 group transition-all'
          >
            <div className='relative w-11 h-11 flex items-center justify-center group-hover:scale-110 transition-transform duration-500'>
              {/* Outer Glow */}
              <div className="absolute inset-0 bg-primary/20 blur-lg rounded-full animate-pulse"></div>
              
              {/* Custom Agno WorkSphere Icon */}
              <svg viewBox="0 0 100 100" className="w-9 h-9 relative z-10 drop-shadow-2xl">
                <defs>
                  <linearGradient id="header-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--color-primary)" />
                    <stop offset="100%" stopColor="var(--color-primary-light, #818cf8)" />
                  </linearGradient>
                </defs>
                
                {/* Orbital Paths */}
                <circle cx="50" cy="50" r="45" fill="none" stroke="url(#header-grad)" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="10 5" className="animate-[spin_20s_linear_infinite]" />
                <circle cx="50" cy="50" r="45" fill="none" stroke="url(#header-grad)" strokeWidth="2" strokeDasharray="20 120" className="animate-[spin_10s_linear_infinite]" />
                
                {/* Tech Nodes */}
                <circle cx="50" cy="5" r="3" fill="var(--color-primary)" />
                <circle cx="95" cy="50" r="3" fill="var(--color-primary-light, #818cf8)" />
                
                {/* Central Sphere Core */}
                <circle cx="50" cy="50" r="22" fill="url(#header-grad)" className="shadow-inner" />
                
                {/* Stylized 'A' Core */}
                <path d="M42 62 L50 38 L58 62 M45 54 L55 54" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className='text-2xl font-black text-slate-800 dark:text-white tracking-tightest'>
              Agno <span className="text-primary">WorkSphere</span>
            </span>
          </Link>
        </div>

        {/* Organization Context Switcher - Desktop */}
        <div className='hidden lg:flex items-center space-x-6'>
          {roleFeatures.canSwitchOrganizations ? (
            <div className='relative' ref={orgDropdownRef}>
              <Button
                variant='ghost'
                onClick={() => {
                  if (!isOrgDropdownOpen) {
                    setIsProjectDropdownOpen(false);
                    // Close other dropdowns if any (user dropdown will be handled similarly if needed)
                    setIsUserDropdownOpen(false); 
                  }
                  setIsOrgDropdownOpen(!isOrgDropdownOpen);
                }}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-micro ${
                  isOrgDropdownOpen ? 'bg-primary/10 text-primary' : ''
                }`}
              >
                <div className='w-6 h-6 bg-muted rounded-sm flex items-center justify-center'>
                  <span className='text-xs font-medium text-text-primary'>
                    {organization?.name?.charAt(0) || 'O'}
                  </span>
                </div>
                <span className='font-medium text-text-primary'>
                  {organization?.name || 'Organization'}
                </span>
                <Icon
                  name='ChevronDown'
                  size={16}
                  className='text-text-secondary'
                />
              </Button>

              {isOrgDropdownOpen && (
                <div className='absolute top-full left-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-elevated z-1010 animate-in fade-in zoom-in-95 duration-200'>
                  <div className='p-2'>
                    <div className='text-xs font-medium text-text-secondary uppercase tracking-wide px-2 py-1'>
                      Switch Organization
                    </div>

                    {/* Create New Organization - Only for Owners */}
                    {actualUserRole === 'owner' && (
                      <>
                        <button
                          onClick={handleCreateOrganization}
                          className='w-full flex items-center space-x-3 px-2 py-2 rounded-sm hover:bg-muted transition-micro text-left border-b border-border mb-2'
                        >
                          <div className='w-8 h-8 bg-primary rounded-sm flex items-center justify-center'>
                            <Icon
                              name='Plus'
                              size={16}
                              className='text-primary-foreground'
                            />
                          </div>
                          <div className='flex-1'>
                            <div className='text-sm font-medium text-primary'>
                              Create New Organization
                            </div>
                            <div className='text-xs text-text-secondary'>
                              Start a new organization
                            </div>
                          </div>
                        </button>
                      </>
                    )}

                    {availableOrganizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => handleOrganizationSwitch(org.id)}
                        className='w-full flex items-center space-x-3 px-2 py-2 rounded-sm hover:bg-muted transition-micro text-left'
                      >
                        <div className='w-8 h-8 bg-muted rounded-sm flex items-center justify-center'>
                          <span className='text-xs font-medium text-text-primary'>
                            {org.name.charAt(0)}
                          </span>
                        </div>
                        <div className='flex-1'>
                          <div className='text-sm font-medium text-text-primary'>
                            {org.name}
                          </div>
                          <div className='text-xs text-text-secondary'>
                            {org.domain}
                          </div>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded ${getRoleBadgeColor(
                            org.role
                          )}`}
                        >
                          {org.role}
                        </span>
                        {org.id === organization?.id && (
                          <Icon
                            name='Check'
                            size={16}
                            className='text-success'
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // For members - show organization name without dropdown
            <div className='flex items-center space-x-2 px-3 py-2'>
              <div className='w-6 h-6 bg-muted rounded-sm flex items-center justify-center'>
                <span className='text-xs font-medium text-text-primary'>
                  {organization?.name?.charAt(0) || 'O'}
                </span>
              </div>
              <span className='font-medium text-text-primary'>
                {organization?.name || 'Organization'}
              </span>
            </div>
          )}

          {/* Role-Based Navigation */}
          <nav className='flex items-center space-x-1'>
            {navigationItems.map((item) => {
              // Special handling for Projects item - make it a dropdown
              if (item.label === 'Projects') {
                return (
                  <div
                    key={item.path}
                    className='relative'
                    ref={projectDropdownRef}
                  >
                    <Button
                      variant='ghost'
                      onClick={() => {
                        if (!isProjectDropdownOpen) {
                          setIsOrgDropdownOpen(false);
                          setIsUserDropdownOpen(false);
                        }
                        setIsProjectDropdownOpen(!isProjectDropdownOpen);
                      }}
                      className={`flex items-center space-x-1 px-2 py-1.5 rounded-md text-xs font-medium transition-micro ${
                        isActivePath(item.path)
                          ? 'bg-primary text-primary-foreground'
                          : isProjectDropdownOpen
                          ? 'bg-primary/10 text-primary'
                          : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                      }`}
                    >
                      <Icon name={item.icon} size={14} />
                      <span>{item.label}</span>
                      <Icon name='ChevronDown' size={10} className='ml-0.5' />
                    </Button>

                    {isProjectDropdownOpen && (
                      <div className='absolute top-full left-0 mt-1 w-80 bg-popover border border-border rounded-md shadow-elevated z-1010 animate-in fade-in zoom-in-95 duration-200'>
                        <div className='p-2'>
                          <div className='flex items-center justify-between px-2 py-1'>
                            <div className='text-xs font-medium text-text-secondary uppercase tracking-wide'>
                              Switch Project
                            </div>
                            {isSwitchingProject && (
                              <div className='flex items-center gap-1 text-xs text-primary'>
                                <Icon name='Loader2' size={12} className='animate-spin' />
                                <span>Switching...</span>
                              </div>
                            )}
                          </div>

                          {/* Create New Project - Only for users with permission */}


                          {/* Available Projects */}
                          {availableProjects.length === 0 ? (
                            <div className='px-2 py-4 text-center'>
                              <div className='w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3'>
                                <Icon
                                  name='FolderOpen'
                                  size={20}
                                  className='text-text-secondary'
                                />
                              </div>
                              <div className='text-sm font-medium text-text-primary mb-1'>
                                No Projects Yet
                              </div>
                              <div className='text-xs text-text-secondary mb-3'>
                                {canCreateProjects
                                  ? 'Create your first project to get started'
                                  : 'Ask your admin to create a project or invite you to one'}
                              </div>

                            </div>
                          ) : (
                            availableProjects.map((project) => (
                              <button
                                key={project.id}
                                onClick={() => handleProjectSwitch(project)}
                                disabled={isSwitchingProject}
                                className={`w-full flex items-center space-x-3 px-2 py-2 rounded-sm transition-micro text-left ${
                                  isSwitchingProject
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-muted'
                                }`}
                              >
                                <div className='w-8 h-8 bg-muted rounded-sm flex items-center justify-center'>
                                  <Icon
                                    name='Folder'
                                    size={16}
                                    className='text-text-primary'
                                  />
                                </div>
                                <div className='flex-1 min-w-0'>
                                  <div className='text-sm font-medium text-text-primary truncate'>
                                    {project.name}
                                  </div>
                                  <div className='text-xs text-text-secondary truncate'>
                                    {project.description}
                                  </div>
                                </div>
                                <div className='flex flex-col items-end shrink-0'>
                                  <span
                                    className={`text-xs px-2 py-1 rounded ${
                                      project.status === 'active'
                                        ? 'bg-success/10 text-success'
                                        : project.status === 'planning'
                                        ? 'bg-warning/10 text-warning'
                                        : 'bg-muted text-text-secondary'
                                    }`}
                                  >
                                    {project.status}
                                  </span>
                                  {actualUserRole === 'member' && (
                                    <span className='text-xs text-text-secondary mt-1'>
                                      Assigned
                                    </span>
                                  )}
                                </div>
                                {String(project.id) === String(currentProject?.id) && !isSwitchingProject && (
                                  <Icon
                                    name='Check'
                                    size={16}
                                    className='text-success'
                                  />
                                )}
                                {isSwitchingProject && String(project.id) === String(currentProject?.id) && (
                                  <Icon
                                    name='Loader2'
                                    size={16}
                                    className='text-primary animate-spin'
                                  />
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Regular navigation items
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro ${
                    isActivePath(item.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={item.icon} size={16} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User Profile & Mobile Menu */}
        <div className='flex items-center space-x-2'>
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Notifications - New System */}
          {!disableAsync && <NotificationBell />}

          {/* User Profile Dropdown */}
          <div className='relative z-50' ref={userDropdownRef}>
            <Button
              variant='ghost'
              onClick={() => {
                console.log('👤 HEADER: User dropdown clicked, current state:', isUserDropdownOpen);
                setIsUserDropdownOpen(!isUserDropdownOpen);
              }}
              className='flex items-center space-x-2 px-2 py-2'
            >
              {currentUser?.avatar ? (
                <div className="w-8 h-8 rounded-full overflow-hidden shadow-sm ring-1 ring-white/20">
                  <Image
                    src={currentUser.avatar}
                    alt={currentUser?.displayName || 'User'}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <InitialsBadge
                  name={currentUser?.displayName || currentUser?.email || 'U'}
                  size="sm"
                  className="flex-shrink-0"
                />
              )}
              <div className='hidden md:block text-left'>
                <div className='text-sm font-medium text-text-primary'>
                  {currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : 'User')}
                </div>
                <div className='text-xs text-text-secondary'>
                  {actualUserRole ? actualUserRole.charAt(0).toUpperCase() + actualUserRole.slice(1) : 'Member'}
                </div>
              </div>
              <Icon
                name='ChevronDown'
                size={16}
                className='text-text-secondary'
              />
            </Button>

            {isUserDropdownOpen && (
              <div className='absolute top-full right-0 mt-1 w-56 bg-popover border border-border rounded-md shadow-elevated z-1010'
                   onLoad={() => console.log('👤 HEADER: User dropdown rendered')}
              >
                <div className='p-2'>
                  <div className='px-2 py-2 border-b border-border'>
                    <div className='font-medium text-text-primary'>
                      {currentUser?.displayName || (currentUser?.email ? currentUser.email.split('@')[0] : 'User')}
                    </div>
                    <div className='text-sm text-text-secondary'>
                      {currentUser?.email || 'user@example.com'}
                    </div>
                    <span
                      className={`inline-block text-xs px-2 py-1 rounded mt-1 ${getRoleBadgeColor(
                        actualUserRole
                      )}`}
                    >
                      {actualUserRole ? actualUserRole.charAt(0).toUpperCase() + actualUserRole.slice(1) : 'No Role'}
                    </span>
                  </div>
                  <div className='py-1'>
                    <Link
                      to='/user-profile-settings'
                      onClick={() => setIsUserDropdownOpen(false)}
                      className='w-full flex items-center space-x-2 px-2 py-2 text-sm text-text-primary hover:bg-muted rounded-sm transition-micro'
                    >
                      <Icon name='User' size={16} />
                      <span>Profile Settings</span>
                    </Link>
                    <Link
                      to='/help-support'
                      onClick={() => setIsUserDropdownOpen(false)}
                      className='w-full flex items-center space-x-2 px-2 py-2 text-sm text-text-primary hover:bg-muted rounded-sm transition-micro'
                    >
                      <Icon name='HelpCircle' size={16} />
                      <span>Help & Support</span>
                    </Link>
                    <div className='border-t border-border my-1'></div>
                    <button
                      onClick={(e) => {
                        console.log('🚪 HEADER: Logout button clicked');
                        e.preventDefault();
                        e.stopPropagation();
                        handleLogout();
                      }}
                      className='w-full flex items-center space-x-2 px-2 py-2 text-sm text-destructive hover:bg-muted rounded-sm transition-micro'
                      type="button"
                    >
                      <Icon name='LogOut' size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant='ghost'
            size='icon'
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className='lg:hidden'
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className='lg:hidden border-t border-border bg-surface'
        >
          <div className='px-4 py-2 space-y-1'>
            {/* Create Organization Button for Mobile - Only for Owners */}
            {actualUserRole === 'owner' && (
              <button
                onClick={() => {
                  handleCreateOrganization();
                  setIsMobileMenuOpen(false);
                }}
                className='w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-micro mb-2'
              >
                <Icon name='Building2' size={16} />
                <span>New Organization</span>
              </button>
            )}

            {/* Create Project Button for Mobile */}
            {roleFeatures.canCreateProjects && (
              <button
                onClick={() => {
                  navigate('/project-management');
                  setIsMobileMenuOpen(false);
                }}
                className='w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-micro'
              >
                <Icon name='Plus' size={16} />
                <span>New Project</span>
              </button>
            )}

            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro ${
                  isActivePath(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
              >
                <Icon name={item.icon} size={16} />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateProjectModalOpen}
        onClose={() => setIsCreateProjectModalOpen(false)}
        onCreateProject={handleCreateProjectSubmit}
        organizationId={
          organization?.id ||
          sessionService.getOrganizationId() ||
          sessionStorage.getItem('organizationId')
        }
        organizationName={organization?.name || 'Organization'}
      />

      {/* Create Organization Modal */}
      <CreateOrganizationModal
        isOpen={isCreateOrganizationModalOpen}
        onClose={() => setIsCreateOrganizationModalOpen(false)}
        onCreateOrganization={handleCreateOrganizationSubmit}
      />
    </header>
  );
};

export default RoleBasedHeader;
